import { useState, useMemo, KeyboardEvent } from "react";

import { Checkbox } from "primereact/checkbox";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { Chip } from "primereact/chip";
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";

import {
  FALLBACK_COLLECTION_CONTENT_TYPE_OPTIONS,
  FALLBACK_CONTENT_TYPE_OPTIONS,
  FALLBACK_CUSTOM_COLLECTIONS_OPTIONS,
  RELEASE_OPTIONS,
} from "sj-ui-toolkit/src/constants";
import { getSelectedTagLabel } from "sj-ui-toolkit/src/utils";

import type { CatalogFilters, CatalogTags, CweTag, FilterCategory } from "../../catalogTypes";

import S from "./FilterPanel.module.scss";

// Frontend-stored filter options

interface FilterPanelProps {
  isOpen: boolean;
  onToggle: () => void;
  tags?: CatalogTags;
  filters: CatalogFilters;
  onFilterChange: (category: FilterCategory, values: string[]) => void;
  onClearFilters: () => void;
  cweTags?: CweTag[];
  hideReleaseFilter?: boolean;
}

type ActiveFilterChip =
  | { key: string; label?: string; category: FilterCategory; value: string; isGroup?: false }
  | { key: string; label?: string; category: FilterCategory; isGroup: true };

const FilterPanel = ({
  isOpen,
  onToggle,
  tags = {},
  filters,
  onFilterChange,
  onClearFilters,
  cweTags = [],
  hideReleaseFilter = false,
}: FilterPanelProps) => {
  const [languageSearch, setLanguageSearch] = useState("");
  const [technologySearch, setTechnologySearch] = useState("");
  const [cweSearch, setCweSearch] = useState("");

  const contentTypeOptions =
    tags.contentType && tags.contentType.length > 0
      ? tags.contentType
      : FALLBACK_CONTENT_TYPE_OPTIONS;

  const collectionContentTypesOptions =
    tags.collectionContentTypes && tags.collectionContentTypes.length > 0
      ? tags.collectionContentTypes
      : FALLBACK_COLLECTION_CONTENT_TYPE_OPTIONS;

  const customCollectionsOptions =
    tags.customCollections && tags.customCollections.length > 0
      ? tags.customCollections
      : FALLBACK_CUSTOM_COLLECTIONS_OPTIONS;

  const focusOptions = tags.focus || [];
  const difficultyOptions = tags.difficulty || [];
  const languageOptions = tags.languages || [];
  const technologyOptions = tags.technologies || [];

  // Filter arrays based on search
  const filteredLanguages = useMemo(() => {
    if (!tags.languages) return [];
    if (!languageSearch) return tags.languages;
    const search = languageSearch.toLowerCase();
    return tags.languages.filter(
      lang =>
        lang.id.toLowerCase().includes(search) || lang.displayName.toLowerCase().includes(search)
    );
  }, [tags.languages, languageSearch]);

  const filteredTechnologies = useMemo(() => {
    if (!tags.technologies) return [];
    if (!technologySearch) return tags.technologies;
    const search = technologySearch.toLowerCase();
    return tags.technologies.filter(
      tech =>
        tech.id.toLowerCase().includes(search) || tech.displayName.toLowerCase().includes(search)
    );
  }, [tags.technologies, technologySearch]);

  const filteredCwe = useMemo(() => {
    if (!cweTags || cweTags.length === 0) return [];
    if (!cweSearch) return cweTags;
    const search = cweSearch.toLowerCase();
    return cweTags.filter(
      cwe =>
        cwe.value.toString().includes(search) ||
        cwe.name.toLowerCase().includes(search) ||
        (cwe.description && cwe.description.toLowerCase().includes(search))
    );
  }, [cweTags, cweSearch]);

  // Handle checkbox change
  const handleCheckboxChange = (category: FilterCategory, value: string) => {
    const currentValues = filters[category] || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    onFilterChange(category, newValues);
  };

  // Native checkboxes only toggle on Space; also toggle on Enter for keyboard a11y.
  const handleCheckboxKeyDown = (
    e: KeyboardEvent<HTMLInputElement>,
    category: FilterCategory,
    value: string
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleCheckboxChange(category, value);
    }
  };

  // Check if a filter is selected
  const isChecked = (category: FilterCategory, value: string) => {
    return (filters[category] || []).includes(value);
  };

  const getCweDisplayName = (cweValue: string) => `CWE-${cweValue}`;

  // Calculate active filter count and generate preview chips
  const getActiveFiltersInfo = () => {
    const activeFilters: ActiveFilterChip[] = [];
    let totalCount = 0;

    // Add individual filters (non-grouped)
    (["focus", "difficulty", "release", "contentType"] as const).forEach(category => {
      const values = filters[category] || [];
      values.forEach(value => {
        totalCount++;
        if (activeFilters.length < 5) {
          let label: string | undefined = value;
          if (category === "focus") label = getSelectedTagLabel(tags.focus, value);
          if (category === "difficulty") label = getSelectedTagLabel(tags.difficulty, value);
          if (category === "contentType") {
            const option = contentTypeOptions.find(o => o.id === value);
            label = option ? option.displayName : value;
          }
          if (category === "release") {
            const option = RELEASE_OPTIONS.find(o => o.value === value);
            label = option ? option.label : value;
          }
          activeFilters.push({ key: `${category}-${value}`, label, category, value });
        }
      });
    });

    // Languages
    const selectedLanguages = filters.languages || [];
    if (selectedLanguages.length > 0) {
      totalCount++;
      if (activeFilters.length < 5) {
        if (selectedLanguages.length === 1) {
          const lang = selectedLanguages[0]!;
          activeFilters.push({
            key: `languages-${lang}`,
            label: getSelectedTagLabel(tags.languages, lang),
            category: "languages",
            value: lang,
          });
        } else {
          activeFilters.push({
            key: "languages-group",
            label: `${selectedLanguages.length} Languages`,
            category: "languages",
            isGroup: true,
          });
        }
      }
    }

    // Technologies
    const selectedTechnologies = filters.technologies || [];
    if (selectedTechnologies.length > 0) {
      totalCount++;
      if (activeFilters.length < 5) {
        if (selectedTechnologies.length === 1) {
          const tech = selectedTechnologies[0]!;
          activeFilters.push({
            key: `technologies-${tech}`,
            label: getSelectedTagLabel(tags.technologies, tech),
            category: "technologies",
            value: tech,
          });
        } else {
          activeFilters.push({
            key: "technologies-group",
            label: `${selectedTechnologies.length} Technologies`,
            category: "technologies",
            isGroup: true,
          });
        }
      }
    }

    // CWEs
    const selectedCwe = filters.cwe || [];
    if (selectedCwe.length > 0) {
      totalCount++;
      if (activeFilters.length < 5) {
        if (selectedCwe.length === 1) {
          const cwe = selectedCwe[0]!;
          activeFilters.push({
            key: `cwe-${cwe}`,
            label: getCweDisplayName(cwe),
            category: "cwe",
            value: cwe,
          });
        } else {
          activeFilters.push({
            key: "cwe-group",
            label: `${selectedCwe.length} CWEs`,
            category: "cwe",
            isGroup: true,
          });
        }
      }
    }

    const selectedCustomCollections = filters.customCollections || [];
    selectedCustomCollections.forEach(value => {
      totalCount++;
      if (activeFilters.length < 5) {
        const option = customCollectionsOptions.find(o => o.id === value);
        activeFilters.push({
          key: `customCollections-${value}`,
          label: option ? option.displayName : value,
          category: "customCollections",
          value,
        });
      }
    });

    const selectedCollectionContentTypes = filters.collectionContentTypes || [];
    if (selectedCollectionContentTypes.length > 0) {
      totalCount++;
      if (activeFilters.length < 5) {
        if (selectedCollectionContentTypes.length === 1) {
          const val = selectedCollectionContentTypes[0]!;
          const option = collectionContentTypesOptions.find(o => o.id === val);
          activeFilters.push({
            key: `collectionContentTypes-${val}`,
            label: option ? option.displayName : val,
            category: "collectionContentTypes",
            value: val,
          });
        } else {
          activeFilters.push({
            key: "collectionContentTypes-group",
            label: `${selectedCollectionContentTypes.length} Collection Types`,
            category: "collectionContentTypes",
            isGroup: true,
          });
        }
      }
    }

    const remainingCount = totalCount - activeFilters.length;

    return { activeFilters, totalCount, remainingCount };
  };

  const { activeFilters, totalCount, remainingCount } = getActiveFiltersInfo();

  const handleRemoveFilter = (filter: ActiveFilterChip) => {
    if (filter.isGroup) {
      onFilterChange(filter.category, []);
    } else {
      handleCheckboxChange(filter.category, filter.value);
    }
  };

  const hasAnyVisibleFilters =
    focusOptions.length > 1 ||
    difficultyOptions.length > 1 ||
    !hideReleaseFilter ||
    contentTypeOptions.length > 1 ||
    customCollectionsOptions.length > 1 ||
    collectionContentTypesOptions.length > 1 ||
    languageOptions.length > 1 ||
    technologyOptions.length > 1 ||
    cweTags.length > 1;

  if (!hasAnyVisibleFilters) {
    return null;
  }

  return (
    <div className={S.filterPanel} data-testid="filter-panel">
      <h2 className="sr-only">Filters</h2>
      <div className={S.filterHeader}>
        <button
          className={S.filterToggle}
          data-testid="filter-panel-toggle-button"
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-controls="catalog-filter-content"
        >
          <i className={`pi ${isOpen ? "pi-chevron-up" : "pi-chevron-down"}`} aria-hidden="true" />
          <span className={S.filterTitle}>Filters</span>
        </button>

        {totalCount > 0 && (
          <div className={S.filterPreview}>
            {activeFilters.map(filter => (
              <Chip
                key={filter.key}
                label={filter.label}
                removable
                data-testid={`filter-chip-${filter.key}`}
                onRemove={() => {
                  handleRemoveFilter(filter);
                  // Chip hides itself on a truthy return; the chip list is state-driven,
                  // so keep the pre-conversion (falsy) behavior.
                  return false;
                }}
                className={S.previewChip}
              />
            ))}
            {remainingCount > 0 && <span className={S.moreFilters}>+{remainingCount} more</span>}
            <Button
              label="Clear filters"
              link
              data-testid="filter-panel-clear-button"
              className={S.clearButton}
              onClick={onClearFilters}
            />
          </div>
        )}
      </div>

      {isOpen && (
        <div
          id="catalog-filter-content"
          className={S.filterContent}
          role="region"
          aria-label="Filter options"
        >
          <div className={S.filterGrid}>
            {/* Audience/Focus */}
            {focusOptions.length > 1 && (
              <div className={S.filterSection}>
                <h3 className={S.sectionTitle}>Audience/Focus</h3>
                <div className={S.checkboxList}>
                  {focusOptions.map(focus => (
                    <div key={focus.id} className={S.checkboxItem}>
                      <Checkbox
                        inputId={`focus-${focus.id}`}
                        pt={{ input: { "data-testid": `filter-checkbox-focus-${focus.id}` } }}
                        checked={isChecked("focus", focus.id)}
                        onChange={() => handleCheckboxChange("focus", focus.id)}
                        onKeyDown={e => handleCheckboxKeyDown(e, "focus", focus.id)}
                      />
                      <label htmlFor={`focus-${focus.id}`} className={S.checkboxLabel}>
                        {focus.displayName}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Difficulty / Release */}
            {(difficultyOptions.length > 1 || !hideReleaseFilter) && (
              <div className={S.filterSection}>
                {difficultyOptions.length > 1 && (
                  <>
                    <h3 className={S.sectionTitle}>Difficulty</h3>
                    <div className={S.checkboxList}>
                      {difficultyOptions.map(diff => (
                        <div key={diff.id} className={S.checkboxItem}>
                          <Checkbox
                            inputId={`difficulty-${diff.id}`}
                            pt={{
                              input: { "data-testid": `filter-checkbox-difficulty-${diff.id}` },
                            }}
                            checked={isChecked("difficulty", diff.id)}
                            onChange={() => handleCheckboxChange("difficulty", diff.id)}
                            onKeyDown={e => handleCheckboxKeyDown(e, "difficulty", diff.id)}
                          />
                          <label htmlFor={`difficulty-${diff.id}`} className={S.checkboxLabel}>
                            {diff.displayName}
                          </label>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {!hideReleaseFilter && (
                  <>
                    <h3
                      className={`${S.sectionTitle} ${difficultyOptions.length > 1 ? S.subSection : ""}`}
                    >
                      Release
                    </h3>
                    <div className={S.checkboxList}>
                      {RELEASE_OPTIONS.map(option => (
                        <div key={option.value} className={S.checkboxItem}>
                          <Checkbox
                            inputId={`release-${option.value}`}
                            pt={{
                              input: { "data-testid": `filter-checkbox-release-${option.value}` },
                            }}
                            checked={isChecked("release", option.value)}
                            onChange={() => handleCheckboxChange("release", option.value)}
                            onKeyDown={e => handleCheckboxKeyDown(e, "release", option.value)}
                          />
                          <label htmlFor={`release-${option.value}`} className={S.checkboxLabel}>
                            {option.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Content Type */}
            {contentTypeOptions.length > 1 && (
              <div className={S.filterSection}>
                <h3 className={S.sectionTitle}>Content Type</h3>
                <div className={S.checkboxList}>
                  {contentTypeOptions.map(option => (
                    <div key={option.id} className={S.checkboxItem}>
                      <Checkbox
                        inputId={`contentType-${option.id}`}
                        pt={{
                          input: { "data-testid": `filter-checkbox-contentType-${option.id}` },
                        }}
                        checked={isChecked("contentType", option.id)}
                        onChange={() => handleCheckboxChange("contentType", option.id)}
                        onKeyDown={e => handleCheckboxKeyDown(e, "contentType", option.id)}
                      />
                      <label
                        htmlFor={`contentType-${option.id}`}
                        className={`${S.checkboxLabel} content-type-tooltip-target`}
                        data-pr-tooltip={
                          option.description
                            ? `${option.displayName}\n\n${option.description}`
                            : option.displayName
                        }
                      >
                        {option.displayName}
                      </label>
                    </div>
                  ))}
                </div>
                <Tooltip
                  target=".content-type-tooltip-target"
                  position="right"
                  className={S.cweTooltip}
                />
              </div>
            )}

            {/* Collections */}
            {(customCollectionsOptions.length > 1 || collectionContentTypesOptions.length > 1) && (
              <div className={S.filterSection}>
                {customCollectionsOptions.length > 1 && (
                  <>
                    <h3 className={S.sectionTitle}>Path Type</h3>
                    <div className={S.checkboxList}>
                      {customCollectionsOptions.map(option => (
                        <div key={option.id} className={S.checkboxItem}>
                          <Checkbox
                            inputId={`customCollections-${option.id}`}
                            pt={{
                              input: {
                                "data-testid": `filter-checkbox-customCollections-${option.id}`,
                              },
                            }}
                            checked={isChecked("customCollections", option.id)}
                            onChange={() => handleCheckboxChange("customCollections", option.id)}
                            onKeyDown={e =>
                              handleCheckboxKeyDown(e, "customCollections", option.id)
                            }
                          />
                          <label
                            htmlFor={`customCollections-${option.id}`}
                            className={S.checkboxLabel}
                          >
                            {option.displayName}
                          </label>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {collectionContentTypesOptions.length > 1 && (
                  <>
                    <h3
                      className={`${S.sectionTitle} ${
                        customCollectionsOptions.length > 1 ? S.subSection : ""
                      }`}
                    >
                      Path Content
                    </h3>
                    <div className={S.checkboxList}>
                      {collectionContentTypesOptions.map(option => (
                        <div key={option.id} className={S.checkboxItem}>
                          <Checkbox
                            inputId={`collectionContentTypes-${option.id}`}
                            pt={{
                              input: {
                                "data-testid": `filter-checkbox-collectionContentTypes-${option.id}`,
                              },
                            }}
                            checked={isChecked("collectionContentTypes", option.id)}
                            onChange={() =>
                              handleCheckboxChange("collectionContentTypes", option.id)
                            }
                            onKeyDown={e =>
                              handleCheckboxKeyDown(e, "collectionContentTypes", option.id)
                            }
                          />
                          <label
                            htmlFor={`collectionContentTypes-${option.id}`}
                            className={S.checkboxLabel}
                          >
                            {option.displayName}
                          </label>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Language (Searchable) */}
            {languageOptions.length > 1 && (
              <div className={S.filterSection}>
                <h3 id="filter-language-title" className={S.sectionTitle}>
                  Language
                </h3>
                <div className={S.searchInput}>
                  <IconField iconPosition="left">
                    <InputIcon className="pi pi-search" />
                    <InputText
                      value={languageSearch}
                      onChange={e => setLanguageSearch(e.target.value)}
                      placeholder="Search languages"
                      aria-label="Search languages"
                      data-testid="filter-language-search-input"
                      className={S.filterSearch}
                    />
                  </IconField>
                </div>
                <div className={S.checkboxList}>
                  {filteredLanguages.map(lang => (
                    <div key={lang.id} className={S.checkboxItem}>
                      <Checkbox
                        inputId={`language-${lang.id}`}
                        pt={{ input: { "data-testid": `filter-checkbox-language-${lang.id}` } }}
                        checked={isChecked("languages", lang.id)}
                        onChange={() => handleCheckboxChange("languages", lang.id)}
                        onKeyDown={e => handleCheckboxKeyDown(e, "languages", lang.id)}
                      />
                      <label htmlFor={`language-${lang.id}`} className={S.checkboxLabel}>
                        {lang.displayName}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Technology (Searchable) */}
            {technologyOptions.length > 1 && (
              <div className={S.filterSection}>
                <h3 id="filter-technology-title" className={S.sectionTitle}>
                  Technology
                </h3>
                <div className={S.searchInput}>
                  <IconField iconPosition="left">
                    <InputIcon className="pi pi-search" />
                    <InputText
                      value={technologySearch}
                      onChange={e => setTechnologySearch(e.target.value)}
                      placeholder="Search technologies"
                      aria-label="Search technologies"
                      data-testid="filter-technology-search-input"
                      className={S.filterSearch}
                    />
                  </IconField>
                </div>
                <div className={S.checkboxList}>
                  {filteredTechnologies.map(tech => (
                    <div key={tech.id} className={S.checkboxItem}>
                      <Checkbox
                        inputId={`technology-${tech.id}`}
                        pt={{ input: { "data-testid": `filter-checkbox-technology-${tech.id}` } }}
                        checked={isChecked("technologies", tech.id)}
                        onChange={() => handleCheckboxChange("technologies", tech.id)}
                        onKeyDown={e => handleCheckboxKeyDown(e, "technologies", tech.id)}
                      />
                      <label htmlFor={`technology-${tech.id}`} className={S.checkboxLabel}>
                        {tech.displayName}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CWE (Searchable) - Updated with tooltips */}
            {cweTags.length > 1 && (
              <div className={S.filterSection}>
                <h3 id="filter-cwe-title" className={S.sectionTitle}>
                  <abbr title="Common Weakness Enumeration" className={S.abbrTitle}>
                    CWE
                  </abbr>
                </h3>
                <div className={S.searchInput}>
                  <IconField iconPosition="left">
                    <InputIcon className="pi pi-search" />
                    <InputText
                      value={cweSearch}
                      onChange={e => setCweSearch(e.target.value)}
                      placeholder="Search by CWE number or name"
                      aria-label="Search by CWE number or name"
                      data-testid="filter-cwe-search-input"
                      className={S.filterSearch}
                    />
                  </IconField>
                </div>
                <div className={S.checkboxList}>
                  {filteredCwe.map(cwe => (
                    <div key={cwe.value} className={S.checkboxItem}>
                      <Checkbox
                        inputId={`cwe-${cwe.value}`}
                        pt={{ input: { "data-testid": `filter-checkbox-cwe-${cwe.value}` } }}
                        checked={isChecked("cwe", cwe.value)}
                        onChange={() => handleCheckboxChange("cwe", cwe.value)}
                        onKeyDown={e => handleCheckboxKeyDown(e, "cwe", cwe.value)}
                      />
                      <label
                        htmlFor={`cwe-${cwe.value}`}
                        className={`${S.checkboxLabel} ${S.cweLabel} cwe-tooltip-target`}
                        data-pr-tooltip={
                          cwe.description ? `${cwe.name}\n\n${cwe.description}` : cwe.name
                        }
                      >
                        {cwe.name}
                      </label>
                    </div>
                  ))}
                </div>
                <Tooltip target=".cwe-tooltip-target" position="left" className={S.cweTooltip} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterPanel;
